import { LitElement, html, css } from 'lit';
import { property, customElement, state } from 'lit/decorators.js';
import '@maersk-global/mds-components-core/mc-icon';
import "@maersk-global/mds-components-core/mc-step-indicator";
import "@maersk-global/mds-components-core/mc-step-indicator-item";

export interface StepData {
    correlationId: string;
    requestNo: number;
    log: string;
    timestamp: string;
    isValid: boolean;
}
@customElement('voy-wr-status-tag')
export class VoyWrStatusTag extends LitElement {
    @property({ type: Boolean }) required: boolean = false;
    @property({ type: String }) statusText: string = '';
    @property({ type: String }) timestamp: string = '';
    @property({ type: Boolean }) loading: boolean = false;
    @property({ type: Boolean }) showIcon: boolean = false;
    @property({ type: Array }) progressData: StepData[] = [];
    @state() private _isExpanded: boolean = false;

    private async _toggleExpanded() {
        this._isExpanded = !this._isExpanded;

        // Wait for DOM update, then notify parent to recalculate heights
        await this.updateComplete;
        this.dispatchEvent(new CustomEvent('wr-status-toggled', { 
            bubbles: true, 
            composed: true 
        }));
    }

    static get styles() {
        return css`
            :host {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                width: 100%;
            }

            .wr-status-tag {
                font-family: MaerskText;
                font-size: 12px;
                font-weight: 400;
                border-radius: 100px;
                text-align: left;
                display: flex;
                padding: 8px 12px;
                justify-content: space-between;
                align-items: center;
                min-height: 20px;
                background-color: #ededed;
                color: #333;
                gap: 8px;
                width: 100%;
                box-sizing: border-box;
            }

            .wr-status-tag__content {
                flex: 1;
                display: inline;
                flex-wrap: wrap;
                padding-left: 8px;
            }

            .wr-status-tag__text {
                font-weight: 500;
                color: #000;
                line-height: 1.4;
            }

            .wr-status-tag__timestamp {
                font-weight: 400;
                color: #666;
                opacity: 0.8;
                padding-left: 4px;
            }

            .wr-status-tag__icon {
                flex-shrink: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                transition: transform 0.3s ease;
            }

            .wr-status-tag__icon--expanded {
                transform: rotate(180deg);
            }

            mc-icon {
                --mc-icon-size: 16px;
            }

            .wr-status-tag--loading {
                justify-content: center;
            }

            .wr-status-tag--not-required {
                width: fit-content;
                justify-content: center;
            }

            .wr-status-tag--not-required .wr-status-tag__text {
                padding-left: 0;
                text-align: center;
            }

            .wr-status-tag--fallback {
                justify-content: center;
            }

            .wr-status-tag--fallback .wr-status-tag__content {
                flex: 0 1 auto;
                padding-left: 0;
                text-align: center;
            }

            .wr-steps-container {
                margin-top: 8px;
                padding: 4px;
                box-sizing: border-box;
                max-height: 180px;
                overflow-y: auto;
            }

            .wr-steps-header {
                font-family: MaerskText;
                font-size: 14px;
                font-weight: 600;
                margin-bottom: 16px;
                color: #000;
            }

            .wr-step {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                margin-bottom: 20px;
                position: relative;
            }

            .wr-step:last-child {
                margin-bottom: 0;
            }

            .wr-step::before {
                content: '';
                position: absolute;
                left: 15px;
                top: 32px;
                bottom: -20px;
                width: 2px;
                background-color: #ddd;
            }

            .wr-step:last-child::before {
                display: none;
            }

            .wr-step__indicator {
                flex-shrink: 0;
                width: 32px;
                height: 32px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 600;
                font-size: 12px;
                background-color: #fff;
                border: 2px solid #ddd;
                color: #666;
                position: relative;
                z-index: 1;
            }

            .wr-step--completed .wr-step__indicator {
                background-color: #0066cc;
                border-color: #0066cc;
                color: #fff;
            }

            .wr-step--completed .wr-step__indicator mc-icon {
                --mc-icon-color: #fff;
            }

            .wr-step--current .wr-step__indicator {
                background-color: #fff;
                border-color: #0066cc;
                color: #0066cc;
                border-width: 3px;
            }

            .wr-step__content {
                flex: 1;
                padding-top: 4px;
            }

            .wr-step__label {
                font-family: MaerskText;
                font-size: 13px;
                font-weight: 500;
                color: #000;
                margin-bottom: 4px;
                line-height: 1.4;
                height: 45px;
            }

            .wr-step__timestamp {
                font-family: MaerskText;
                font-size: 12px;
                font-weight: 400;
                color: #666;
            }

            .wr-step--upcoming .wr-step__label {
                color: #666;
            }

            /* Scrollbar styling - hidden by default, visible on hover */
            .wr-steps-container {
                scrollbar-width: thin;
                scrollbar-color: transparent transparent;
            }

            .wr-steps-container:hover {
                scrollbar-color: #888 #f1f1f1;
            }

            .wr-steps-container::-webkit-scrollbar {
                width: 6px;
            }

            .wr-steps-container::-webkit-scrollbar-track {
                background: transparent;
                border-radius: 10px;
            }

            .wr-steps-container:hover::-webkit-scrollbar-track {
                background: #f1f1f1;
            }

            .wr-steps-container::-webkit-scrollbar-thumb {
                background: transparent;
                border-radius: 10px;
            }

            .wr-steps-container:hover::-webkit-scrollbar-thumb {
                background: #888;
            }

            .wr-steps-container::-webkit-scrollbar-thumb:hover {
                background: #555;
            }

            .wr-step-label {
                width: 280px;
                font-size: 12px;
                display: inline;
                line-height: 1.4;
                height: 45px;
            }

            .wr-step-label__text {
                font-family: MaerskText;
                font-size: 12px;
                font-weight: 500;
                color: #000;
            }

            .wr-step-label__timestamp {
                font-family: MaerskText;
                font-size: 12px;
                font-weight: 400;
                color: #666;
            }

            .wr-status-header {
                font-weight: 600;
                padding-bottom: 8px;
                padding-left: 4px;
            }

            .request-icon {
                color: white;
                font-family: 'Maersk Text';
            }
 
            .request-icon--error {
                background-color: #999;
                font-family: 'Maersk Text';
                color: white;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                height: 100%;
                border-radius: 50%;
            }

            .wr-step-label--error .wr-step-label__text {
                color: #999;
            }
        `;
    }

    render() {
        if (this.loading) {
            return html`
                <div class="wr-status-tag wr-status-tag--loading">
                    Loading...
                </div>
            `;
        }

        if (!this.required) {
            return html`
                <div class="wr-status-tag wr-status-tag--not-required">
                    <div class="wr-status-tag__content">
                        <div class="wr-status-tag__text">WR not required</div>
                    </div>
                </div>
            `;
        }

        // Sort progressData by timestamp descending (latest first)
        const sortedSteps = [...(this.progressData || [])].sort((a, b) => {
            const dateA = new Date(a.timestamp).getTime();
            const dateB = new Date(b.timestamp).getTime();
            return dateB - dateA;
        });

        // Get the latest record for display
        const latestStep = sortedSteps.length > 0 ? sortedSteps[0] : null;

        return html`
            <div style="width:100%;">
                <div class="wr-status-header">WR Advice Request Status</div>
                <div class="wr-status-tag ${latestStep ? '' : 'wr-status-tag--fallback'}">
                    <div class="wr-status-tag__content">
                        <span class="wr-status-tag__text"> ${latestStep ? '(R' + latestStep.requestNo + ') - ' + latestStep.log : this.statusText}</span>
                        ${latestStep ? html`<span class="wr-status-tag__timestamp">- ${latestStep.timestamp} UTC</span>` : 
                          this.timestamp ? html`<span class="wr-status-tag__timestamp">- ${this.timestamp} UTC</span>` : ''}
                    </div>
                    ${this.showIcon && sortedSteps.length > 0 ? html`
                        <div 
                            class="wr-status-tag__icon ${this._isExpanded ? 'wr-status-tag__icon--expanded' : ''}"
                            @click=${this._toggleExpanded}
                        >
                            <mc-icon icon="caret-down" height="16px" width="16px"></mc-icon>
                        </div>
                    ` : ''}
                </div>
            </div>
            
            ${this._isExpanded && sortedSteps.length > 0 ? html`
                <div class="wr-steps-container">
                    <mc-step-indicator class="progress-bar" orientation="vertical" fit="large">
                    ${sortedSteps.slice(1).map(
                        (step, index) => html`
                        <mc-step-indicator-item state=${step.isValid ? 'completed' : 'pending'}>
                            <span class=${step.isValid ? 'request-icon' : 'request-icon--error'} slot="icon">R${step.requestNo}</span>
                            <span class='wr-step-label ${!step.isValid ? 'wr-step-label--error' : ''}'>
                                <span class="wr-step-label__text">${step.log} </span>
                                <span class="wr-step-label__timestamp">- ${step.timestamp} UTC</span>
                            </span>
                        </mc-step-indicator-item>
                        `
                    )}
                </mc-step-indicator>
                </div>
            ` : ''}
        `;
    }
}

